import {
  Button, LinearProgress, Stack, Typography,
} from '@mui/material';
import CateringProvider from 'components/CateringProvider';
import { env } from 'constants';
import { AuthContext, CartContext } from 'constants/contexts';
import React, { useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { priceTransformer } from 'utils';
import { actionTexts, getActionText } from 'utils/cart';
import { getItemTotalPrice } from 'utils/item';

function Action({ onClick = () => { }, ...props }) {
  const {
    items, minimumOrderPrice,
  } = useContext(CartContext);
  const { isLoggedIn, redirectAfterLoggedIn, profileIsComplete } = useContext(AuthContext);
  const totalPrice = useMemo(() => items.reduce((total, item) => total + getItemTotalPrice(item), 0), [items]);
  const history = useHistory();
  const catering = CateringProvider.useCatering();

  const handleCheckout = (e) => {
    if (!isLoggedIn || !profileIsComplete) {
      catering.set({
        checkoutStarted: true,
      });
      redirectAfterLoggedIn();
      return;
    }

    if (onClick) {
      onClick(e);
    }

    if (env.IS_CATERING) {
      catering.continueToCheckout();
    } else {
      history.replace('/order/checkout');
    }
  };
  const disabled = items.length === 0 || totalPrice < minimumOrderPrice;

  function renderButton() {
    if (disabled) return;
    const totalCount = items.reduce(
      (total, { selection: { qty } }) => total + qty,
      0,
    );
    let text = isLoggedIn ? `${actionTexts.ready} (${totalCount}) · ${priceTransformer(totalPrice)}` : actionTexts.notLoggedIn;
    if (env.IS_CATERING) {
      text = (
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
          <Typography variant="label-lg">{getActionText({ isLoggedIn, profileIsComplete })}</Typography>
          <Stack alignItems="end">
            <Typography variant="title-md">{priceTransformer(totalPrice)}</Typography>
            {catering?.headcount > 0 && (
              <Typography variant="body-overline" fontWeight={700}>
                {priceTransformer(totalPrice / (catering?.headcount || 1))}
                {' '}
                / person
              </Typography>
            )}
          </Stack>
        </Stack>
      );
    }
    return (
      <Button
        fullWidth
        variant={isLoggedIn && !profileIsComplete ? 'muted' : 'contained'}
        size="large"
        onClick={handleCheckout}
        disabled={disabled}
        sx={{ height: 56 }}
      >
        {text}
      </Button>
    );
  }

  return (
    <Stack marginTop="auto" {...props} spacing={1.5}>
      {env.IS_CATERING && catering?.headcount < 1 && !disabled && (
        <Typography variant="body-caption" color="text.secondary" sx={{ textAlign: 'center' }}>Add your headcount for cost per person</Typography>
      )}

      {items.length > 0 && totalPrice < minimumOrderPrice && (
        <Stack gap={1}>
          <Typography variant="body-md">
            {priceTransformer(totalPrice)}
          </Typography>
          <LinearProgress
            variant="determinate"
            sx={{
              height: 6,
              borderRadius: 1,
              '&.MuiLinearProgress-determinate': {
                backgroundColor: 'background.elevation2',
              },
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'text.primary',
              },
            }}
            value={(100 * totalPrice) / minimumOrderPrice}
          />
          <Typography variant="body-overline" fontWeight={700} textAlign="right">
            Minimum:
            {' '}
            {priceTransformer(minimumOrderPrice)}
          </Typography>
        </Stack>
      )}

      {renderButton()}
    </Stack>
  );
}

export default Action;
